import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { trackPurchase } from "../lib/pixels";
import { useLang } from "../i18n/LanguageContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const { t, lang } = useLang();
  const [state, setState] = useState({ status: "loading", orderNumber: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", orderNumber: null }); return; }
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          clear();
          trackPurchase(data.order_number, data.total || 0);
          // Suivi de conversion Opinly (dédoublonné par externalEventId = numéro de commande)
          window.opinly?.track("purchase", { value: data.total || 0, currency: "CAD" }, { externalEventId: data.order_number });
          setState({ status: "paid", orderNumber: data.order_number });
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setState({ status: "failed", orderNumber: null });
          return;
        }
      } catch (e) { /* retry */ }
      attempts.current += 1;
      if (attempts.current < 15) setTimeout(poll, 2000);
      else setState({ status: "timeout", orderNumber: null });
    };
    poll();
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="payment-success-page" className="min-h-[70vh] flex items-center justify-center px-6 pt-24 pb-16">
      <div className="text-center max-w-md">
        <span className="bg-ivory rounded-2xl px-5 py-3 inline-flex mb-8">
          <img src={lang === "en" ? "/logo-gold-t-en.png" : "/logo-gold-t.png"} alt="Mr Trophée" className="h-12 w-auto" />
        </span>
        {state.status === "loading" && (
          <>
            <Loader2 className="w-14 h-14 text-gold animate-spin mx-auto mb-6" />
            <h1 className="font-serif text-4xl mb-3">{t("payment.confirming")}</h1>
            <p className="text-ivory/60" data-testid="payment-pending">{t("payment.confirming_sub")}</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-gold mx-auto mb-6" />
            <h1 className="font-serif text-4xl mb-3">{t("payment.confirmed_title")}</h1>
            <p className="text-ivory/60 mb-2" data-testid="payment-confirmed">{t("payment.confirmed")}</p>
            {state.orderNumber && <p className="text-champagne font-serif text-xl mb-2" data-testid="order-number">{t("payment.order_number", { num: state.orderNumber })}</p>}
            <div className="flex gap-4 justify-center mt-8">
              <Link to="/compte" data-testid="goto-account" className="bg-gold text-ink font-semibold px-7 py-3 rounded-full hover:bg-champagne transition-colors duration-300">{t("payment.track")}</Link>
              <Link to="/boutique" className="border border-ivory/25 px-7 py-3 rounded-full hover:border-gold hover:text-gold transition-colors duration-300">{t("payment.catalogue")}</Link>
            </div>
          </>
        )}
        {(state.status === "failed" || state.status === "error" || state.status === "timeout") && (
          <>
            <XCircle className="w-14 h-14 text-red-400 mx-auto mb-6" />
            <h1 className="font-serif text-4xl mb-3">{t("payment.failed_title")}</h1>
            <p className="text-ivory/60 mb-8" data-testid="payment-failed">
              {state.status === "timeout" ? t("payment.timeout") : t("payment.failed")}
            </p>
            <Link to="/panier" data-testid="back-to-cart" className="bg-gold text-ink font-semibold px-7 py-3 rounded-full hover:bg-champagne transition-colors duration-300">{t("payment.back_to_cart")}</Link>
          </>
        )}
      </div>
    </div>
  );
}
